import { Component, EventEmitter, Input, OnInit, Output, HostListener, ElementRef, OnDestroy, Injectable, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup } from '@angular/forms';

@Injectable()
export class YearPickerService {
  
  // Common year validation logic
  validateYearRange(fromYear: number | null, toYear: number | null, fromFieldName: string, toFieldName: string, snackbarService: any): boolean {
    if (fromYear && toYear && fromYear > toYear) {
      snackbarService.openSnackBar(`${fromFieldName} cannot be greater than ${toFieldName}`, 'gtw-snackbar--red');
      return false;
    }
    return true;
  }

  // Common year formatting methods
  formatYearToYYYY(year: any): string {
    if (!year) return '';
    
    // If it's already a 4-digit year string, return as is
    if (typeof year === 'string' && year.length === 4 && /^\d{4}$/.test(year)) {
      return year;
    }
    
    // If it's a date string (YYYY-MM-DD format), extract the year
    if (typeof year === 'string' && year.includes('-')) {
      const yearPart = year.split('-')[0];
      if (yearPart.length === 4 && /^\d{4}$/.test(yearPart)) {
        return yearPart;
      }
    }
    
    // If it's a number, convert to 4-digit string
    if (typeof year === 'number') {
      const yearStr = year.toString();
      if (yearStr.length === 4) {
        return yearStr;
      }
    }
    
    // If it's a Date object, extract the year
    if (year instanceof Date) {
      return year.getFullYear().toString();
    }
    
    // Try to parse as number and format as 4-digit year
    const yearNum = parseInt(year.toString());
    if (!isNaN(yearNum) && yearNum >= 1000 && yearNum <= 9999) {
      return yearNum.toString();
    }
    
    // If all else fails, return empty string
    return '';
  }

  // Common year conversion methods
  convertYearToDate(year: any): string {
    if (!year) return '';
    
    // If it's already a date string, return as is
    if (typeof year === 'string' && year.includes('-')) {
      return year;
    }
    
    // Convert year number to date string (YYYY-01-01 format)
    const yearNum = parseInt(year.toString());
    if (!isNaN(yearNum)) {
      return `${yearNum}-01-01`;
    }
    
    return '';
  }

  formatYear(year: any): string {
    if (!year) return '';
    
    // If it's a date string, extract the year
    if (typeof year === 'string' && year.includes('-')) {
      return new Date(year).getFullYear().toString();
    }
    
    // If it's a number, convert to string
    if (typeof year === 'number') {
      return year.toString();
    }
    
    if (year instanceof Date) {
      return year.getFullYear().toString();
    }
    
    return year.toString();
  }

  // Common year picker state management
  openYearPicker(currentPicker: boolean, otherPicker: boolean): boolean {
    // Close the other picker if it's open
    if (otherPicker) {
      return false; // Signal to close other picker
    }
    return true; // Signal to open current picker
  }

  closeYearPicker(): boolean {
    return false;
  }
}

@Component({
    selector: 'gtw-year-picker-popup',
    templateUrl: './year-picker-popup.component.html',
    styleUrls: ['./year-picker-popup.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class YearPickerPopupComponent implements OnInit, OnDestroy {

  @Input() selectedYear: number | null = null;
  @Input() minYear: number = 1900;
  @Input() maxYear: number = 9999;
  @Input() showPopup: boolean | null = null;
  @Input() triggerElementId: string = '';
  
  @Output() yearSelected = new EventEmitter<number | null>();
  @Output() popupClosed = new EventEmitter<void>();

  yearGrid: number[][] = [];
  currentRangeStart: number = 2020;
  yearsPerPage: number = 20; // Display 20 years at a time (4 rows x 5 columns)
  uniqueId: string = '';

  constructor(private elementRef: ElementRef) { }

  ngOnInit(): void {
    // Generate unique ID for this picker instance
    this.uniqueId = 'year-picker-' + Math.random().toString(36).substr(2, 9);
    this.initializeYearPicker();
  }

  ngOnDestroy(): void {
    // Clean up any global event listeners if needed
  }

  ngOnChanges(): void {
    if (this.showPopup) {
      this.initializeYearPicker();
      // Close all other year pickers when this one opens
      this.closeAllOtherYearPickers();
    }
  }

  initializeYearPicker(): void {
    // Set initial range based on selected year or current year
    const baseYear = this.selectedYear || new Date().getFullYear();
    this.currentRangeStart = Math.floor(baseYear / this.yearsPerPage) * this.yearsPerPage;
    this.generateYearGrid();
  }

  generateYearGrid(): void {
    this.yearGrid = [];
    const rows = 4;
    const cols = 5;
    
    for (let row = 0; row < rows; row++) {
      const yearRow: number[] = [];
      for (let col = 0; col < cols; col++) {
        const year = this.currentRangeStart + (row * cols) + col;
        yearRow.push(year);
      }
      this.yearGrid.push(yearRow);
    }
  }

  navigateYears(direction: 'prev' | 'next'): void {
    if (direction === 'prev') {
      this.currentRangeStart -= this.yearsPerPage;
      if (this.currentRangeStart < this.minYear) {
        this.currentRangeStart = this.minYear;
      }
    } else {
      this.currentRangeStart += this.yearsPerPage;
      if (this.currentRangeStart > this.maxYear - this.yearsPerPage) {
        this.currentRangeStart = this.maxYear - this.yearsPerPage + 1;
      }
    }
    this.generateYearGrid();
  }

  selectYear(year: number): void {
    if (year >= this.minYear && year <= this.maxYear) {
      this.selectedYear = year;
      this.yearSelected.emit(year);
      this.closePopup();
    }
  }

  selectToday(): void {
    const currentYear = new Date().getFullYear();
    this.selectYear(currentYear);
  }

  clearYear(): void {
    this.selectedYear = null;
    this.yearSelected.emit(null);
    this.closePopup();
  }

  closePopup(): void {
    this.showPopup = false;
    this.popupClosed.emit();
  }

  // Close all other year pickers when this one opens
  private closeAllOtherYearPickers(): void {
    // Find all year picker overlays except this one
    const allOverlays = document.querySelectorAll('.year-picker-overlay');
    allOverlays.forEach(overlay => {
      const popup = overlay.querySelector('.year-picker-popup');
      if (popup && popup.id !== this.uniqueId) {
        // Dispatch close event to other picker instances
        const closeEvent = new CustomEvent('year-picker-close');
        popup.dispatchEvent(closeEvent);
      }
    });
  }

  // Static method to close all year pickers globally
  static closeAllYearPickers(): void {
    const allOverlays = document.querySelectorAll('.year-picker-overlay');
    allOverlays.forEach(overlay => {
      const popup = overlay.querySelector('.year-picker-popup');
      if (popup) {
        const closeEvent = new CustomEvent('year-picker-close');
        popup.dispatchEvent(closeEvent);
      }
    });
  }

  // Close popup when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    if (this.showPopup) {
      const target = event.target as HTMLElement;
      
      // Check if click is on a calendar icon (trigger element)
      if (target.closest('.input-group-addon') || target.closest('[class*="calendar"]')) {
        // Don't handle calendar icon clicks here - let the parent component handle them
        // The parent will call closePopup() if needed
        return;
      }
      
      // Check if click is outside this specific popup
      const popupElement = this.elementRef.nativeElement.querySelector('.year-picker-popup');
      if (popupElement && !popupElement.contains(target)) {
        this.closePopup();
      }
    }
  }

  // Listen for close events from other pickers
  @HostListener('document:year-picker-close', ['$event'])
  onOtherPickerClose(_event: Event): void {
    if (this.showPopup) {
      this.closePopup();
    }
  }

  // Close popup on Escape key
  @HostListener('document:keydown.escape', ['$event'])
  onEscapeKey(_event: Event): void {
    if (this.showPopup) {
      this.closePopup();
    }
  }

  isYearSelected(year: number): boolean {
    return this.selectedYear === year;
  }

  isYearDisabled(year: number): boolean {
    return year < this.minYear || year > this.maxYear;
  }

  getYearRangeLabel(): string {
    const endYear = Math.min(this.currentRangeStart + this.yearsPerPage - 1, this.maxYear);
    return `${this.currentRangeStart} - ${endYear}`;
  }

  onYearInputChange(year: number): void {
    if (year && year >= this.minYear && year <= this.maxYear) {
      this.selectedYear = year;
      // Update the year grid to show the selected year's range
      this.currentRangeStart = Math.floor(year / this.yearsPerPage) * this.yearsPerPage;
      this.generateYearGrid();
      // Emit the selected year
      this.yearSelected.emit(year);
    }
  }

}

